import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Container } from "../../../components/Container";
import { CustomButton } from "../../../components/CustomButton";
import { showCustomToast } from "../../../components/CustomToast";
import {
  putUpdateBillingThunk,
  removeBillingExpense,
  updateBillingCurrentStep,
  updateBillingExpectedWasteAmount,
  upsertBillingExpense,
} from "../../../reducers/billingReducer";
import {
  getBillingExpectedExpensePerKg,
  getBillingExpectedExpenses,
  getBillingExpectedExpensesValueSum,
  getBillingExpectedWasteAmount,
  getBillingIsUpdating,
  getCanSaveBillingCosts,
  getIsBillingInNewStatus,
} from "../../../selectors/billing";
import { useAppDispatch } from "../../../store";
import { BillingStep } from "../../../types/billing";
import { sanitizePositiveNumberInput } from "../../../utils/input";
import { BillingExpenses } from "../BillingExpenses/BillingExpenses";
import { BillingWasteWeightInput } from "../BillingWasteWeightInput/BillingWasteWeightInput";

import styles from "./BillingPageCosts.module.scss";

export const BillingPageCosts: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const expectedExpenses = useSelector(getBillingExpectedExpenses);
  const expectedWasteAmount = useSelector(getBillingExpectedWasteAmount);
  const expectedExpensesValueSum = useSelector(getBillingExpectedExpensesValueSum);
  const expectedExpensePerKg = useSelector(getBillingExpectedExpensePerKg);
  const isBillingInNewStatus = useSelector(getIsBillingInNewStatus);
  const canSaveBillingCosts = useSelector(getCanSaveBillingCosts);
  const isUpdating = useSelector(getBillingIsUpdating);

  const handleSave = async () => {
    const result = await dispatch(putUpdateBillingThunk());
    if (putUpdateBillingThunk.fulfilled.match(result)) {
      showCustomToast(t("billingPageCosts.saveSuccess"), { type: "success" });
      dispatch(updateBillingCurrentStep(BillingStep.Calculation));
    } else if (putUpdateBillingThunk.rejected.match(result)) {
      showCustomToast(t(`billingApiErrors.${result.payload}`), { type: "error" });
    }
  };

  const handleContinue = () => {
    dispatch(updateBillingCurrentStep(BillingStep.Calculation));
  };

  return (
    <>
      <Container>
        <BillingWasteWeightInput
          label={t("billingPageCosts.expectedWasteWeight")}
          value={expectedWasteAmount}
          onChange={(newValue) => {
            dispatch(updateBillingExpectedWasteAmount(sanitizePositiveNumberInput(newValue)));
          }}
          isDisabled={!isBillingInNewStatus || isUpdating}
        />
      </Container>
      <BillingExpenses
        label={t("billingPageCosts.expectedExpensesForWaste")}
        expenses={expectedExpenses}
        expensesValueSum={expectedExpensesValueSum}
        costPerKg={expectedExpensePerKg}
        onCreate={() => {
          dispatch(upsertBillingExpense({ real: false }));
        }}
        onChange={(expense) => {
          dispatch(upsertBillingExpense(expense));
        }}
        onRemove={(id) => {
          dispatch(removeBillingExpense(id));
        }}
        isDisabled={!isBillingInNewStatus || isUpdating}
      />
      <div className={styles.buttonContainer}>
        {isBillingInNewStatus ? (
          <CustomButton
            onClick={handleSave}
            text={t("billingPageCosts.save")}
            className={styles.saveBtn}
            disabled={isUpdating || !canSaveBillingCosts}
          />
        ) : (
          <CustomButton onClick={handleContinue} text={t("billingPageCosts.continue")} className={styles.continueBtn} />
        )}
      </div>
    </>
  );
};
